/**
 * ContactPage -- rewritten in the /n2 cream+serif+gold aesthetic (2026-07-28).
 *
 * Both /n2 B2B landings (N2Enterprise, N2Influencer) and the /n2 landing hero
 * link here with ?source=n2-<segment>. We map source -> inquiryType so the
 * dropdown is prefilled correctly, and fall back to the legacy `type` param
 * for any older links still floating around.
 */

import React, { useState, CSSProperties } from 'react';
import { useSearchParams } from 'react-router-dom';
import { N2Frame } from '../n2/N2Frame';
import { palette } from '../n2/theme';
import {
  Eyebrow, PageTitle, PageLede, PrimaryButton, SectionLabel, Body,
} from '../n2/N2Primitives';

const API = process.env.REACT_APP_API_URL || 'https://4jbhzumjk7.execute-api.us-east-2.amazonaws.com/prod';

const INQUIRY_TYPES = [
  { value: 'general',    label: 'General inquiry' },
  { value: 'enterprise', label: 'Managed book / institution' },
  { value: 'influencer', label: 'Content partner (creator, newsletter, community)' },
  { value: 'algo-beta',  label: 'Engine beta preview' },
];

// /n2 CTAs use ?source=n2-<segment>; older links use ?type=<value>.
const SOURCE_TO_TYPE: Record<string, string> = {
  'n2-enterprise': 'enterprise',
  'n2-influencer': 'influencer',
  'n2-landing':    'general',
  'n2-large-books': 'enterprise',
};

function resolveInitialType(searchParams: URLSearchParams): string {
  const source = searchParams.get('source');
  if (source && SOURCE_TO_TYPE[source]) return SOURCE_TO_TYPE[source];
  const legacyType = searchParams.get('type');
  if (legacyType && INQUIRY_TYPES.some(t => t.value === legacyType)) return legacyType;
  return 'general';
}

export const ContactPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [inquiryType, setInquiryType] = useState(() => resolveInitialType(searchParams));
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !message.trim()) {
      setError('Please fill in all fields.');
      return;
    }
    setSubmitting(true); setError('');
    try {
      const resp = await fetch(`${API}/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim(),
          inquiryType,
          message: message.trim(),
          source: searchParams.get('source') || undefined,
        }),
      });
      if (!resp.ok) throw new Error('Failed to send');
      setSuccess(true);
    } catch {
      setError('Failed to send message. Please email us directly at support@wealthfinancialsystems.com');
    } finally {
      setSubmitting(false);
    }
  };

  const inputStyle: CSSProperties = {
    width: '100%', padding: '12px 14px',
    background: palette.paper,
    border: `1px solid ${palette.line}`,
    color: palette.ink,
    fontFamily: 'var(--sans)', fontSize: 15, lineHeight: 1.4,
    borderRadius: 2, outline: 'none',
    transition: 'border-color 0.15s ease',
  };

  const labelStyle: CSSProperties = {
    display: 'block',
    fontFamily: 'var(--sans)', fontSize: 11, fontWeight: 700,
    letterSpacing: '0.18em', textTransform: 'uppercase',
    color: palette.goldDeep, marginBottom: 8,
  };

  const focusOn = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    (e.currentTarget as HTMLElement).style.borderColor = palette.goldDeep;
  };
  const focusOff = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    (e.currentTarget as HTMLElement).style.borderColor = palette.line;
  };

  return (
    <N2Frame page="contact">
      <section style={{
        maxWidth: 720, margin: '0 auto', padding: '80px 32px 32px',
        textAlign: 'center',
      }}>
        <Eyebrow>Contact</Eyebrow>
        <PageTitle italic="in the door.">Start the conversation</PageTitle>
        <div style={{ maxWidth: 560, margin: '0 auto' }}>
          <PageLede>
            Partnership, managed-book evaluation, or a specific research
            question. We'll come back within 24 hours on business days.
          </PageLede>
        </div>
      </section>

      <section style={{
        maxWidth: 720, margin: '0 auto', padding: '0 32px 96px',
      }}>
        <SectionLabel left="Send us a note" right="direct to the desk" />

        {success ? (
          <div style={{
            marginTop: 32,
            background: palette.paper, border: `1px solid ${palette.line}`,
            borderRadius: 4, padding: '40px 32px', textAlign: 'center',
            position: 'relative',
          }}>
            <div style={{
              position: 'absolute', top: 0, left: 32, width: 32, height: 3,
              background: palette.gold,
            }} />
            <h2 style={{
              fontFamily: 'var(--serif)', fontWeight: 500, fontSize: 28,
              margin: '0 0 12px', color: palette.ink, letterSpacing: '-0.01em',
            }}>
              Message received
            </h2>
            <Body>
              Thank you, {name}. We'll be in touch within 24 hours on business days.
            </Body>
            <div style={{ marginTop: 24 }}>
              <PrimaryButton to="/n2">Back to home</PrimaryButton>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} style={{ marginTop: 32 }}>
            <div style={{ marginBottom: 20 }}>
              <label style={labelStyle}>Your name</label>
              <input
                type="text"
                value={name}
                onChange={e => setName(e.target.value)}
                onFocus={focusOn}
                onBlur={focusOff}
                style={inputStyle}
                placeholder="Jane Roberts"
              />
            </div>

            <div style={{ marginBottom: 20 }}>
              <label style={labelStyle}>Email address</label>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                onFocus={focusOn}
                onBlur={focusOff}
                style={inputStyle}
                placeholder="jane@example.com"
              />
            </div>

            <div style={{ marginBottom: 20 }}>
              <label style={labelStyle}>What's this about?</label>
              <select
                value={inquiryType}
                onChange={e => setInquiryType(e.target.value)}
                onFocus={focusOn}
                onBlur={focusOff}
                style={{ ...inputStyle, cursor: 'pointer', appearance: 'auto' }}
              >
                {INQUIRY_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>

            <div style={{ marginBottom: 24 }}>
              <label style={labelStyle}>Message</label>
              <textarea
                value={message}
                onChange={e => setMessage(e.target.value)}
                onFocus={focusOn}
                onBlur={focusOff}
                rows={6}
                style={{ ...inputStyle, resize: 'vertical', minHeight: 140, lineHeight: 1.55 }}
                placeholder="Tell us about your audience, your book, or the specific research you'd want to see..."
              />
            </div>

            {error && (
              <div style={{
                padding: '12px 14px', borderRadius: 2,
                background: '#f7ecec',
                border: `1px solid ${palette.negative}`,
                color: palette.negative,
                fontFamily: 'var(--sans)', fontSize: 14,
                marginBottom: 20,
              }}>
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              style={{
                width: '100%',
                fontFamily: 'var(--sans)', fontSize: 15, fontWeight: 600,
                letterSpacing: '0.02em', padding: '14px 24px',
                background: submitting ? palette.ink3 : palette.ink,
                color: palette.paper,
                border: `1px solid ${submitting ? palette.ink3 : palette.ink}`,
                borderRadius: 2,
                cursor: submitting ? 'wait' : 'pointer',
                transition: 'background-color 0.15s ease, border-color 0.15s ease',
              }}
              onMouseEnter={e => {
                if (submitting) return;
                (e.currentTarget as HTMLElement).style.background = palette.goldDeep;
                (e.currentTarget as HTMLElement).style.borderColor = palette.goldDeep;
              }}
              onMouseLeave={e => {
                if (submitting) return;
                (e.currentTarget as HTMLElement).style.background = palette.ink;
                (e.currentTarget as HTMLElement).style.borderColor = palette.ink;
              }}
            >
              {submitting ? 'Sending…' : 'Send message'}
            </button>
          </form>
        )}

        <div style={{
          marginTop: 40, paddingTop: 32,
          borderTop: `1px solid ${palette.line}`,
          textAlign: 'center',
        }}>
          <div style={{
            fontFamily: 'var(--sans)', fontSize: 11, fontWeight: 700,
            letterSpacing: '0.22em', textTransform: 'uppercase',
            color: palette.ink3, marginBottom: 14,
          }}>
            Or email us directly
          </div>
          <div style={{
            display: 'flex', gap: 24, justifyContent: 'center', flexWrap: 'wrap',
            fontFamily: 'var(--sans)', fontSize: 14,
          }}>
            <a
              href="mailto:support@wealthfinancialsystems.com"
              style={{ color: palette.goldDeep, textDecoration: 'none' }}
            >
              support@wealthfinancialsystems.com
            </a>
            <a
              href="mailto:enterprise@wealthfinancialsystems.com"
              style={{ color: palette.goldDeep, textDecoration: 'none' }}
            >
              enterprise@wealthfinancialsystems.com
            </a>
          </div>
        </div>
      </section>
    </N2Frame>
  );
};

export default ContactPage;
